import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PiPencilSimple, PiPlus, PiTag, PiToggleLeft, PiToggleRight } from 'react-icons/pi';
import AccountPageHeader from '../components/AccountPageHeader';
import PageIntro from '../components/PageIntro';
import CategoryFormSheet from '../components/CategoryFormSheet';
import { useCategories } from '../hooks/useCategories';
import { useFeedback } from '../FeedbackContext';
import { remoteOperationMessage } from '../services/connectivityService';
import './Categories.css';

const TYPE_LABELS = { income: 'Receita', expense: 'Despesa', both: 'Receita e despesa' };

const Categories = ({ currentAccountId, currentRole }) => {
  const navigate = useNavigate();
  const { customCategories, loading, error, createCategory, updateCategory, setCategoryActive } = useCategories(currentAccountId);
  const { showSuccess, showError } = useFeedback();
  const [editor, setEditor] = useState(null);
  const canManage = ['owner', 'admin', 'editor'].includes(currentRole);

  const saveCategory = async (input) => {
    if (editor?.id) {
      await updateCategory({ categoryId: editor.id, ...input });
      showSuccess('A categoria foi atualizada.', 'Categoria atualizada');
    } else {
      await createCategory(input);
      showSuccess('A categoria já está disponível nesta conta.', 'Categoria criada');
    }
  };

  const toggleActive = async (category) => {
    try {
      await setCategoryActive(category.id, !category.active);
      showSuccess(
        category.active ? 'A categoria foi desativada sem apagar o histórico.' : 'A categoria voltou a ficar disponível.',
        category.active ? 'Categoria desativada' : 'Categoria ativada'
      );
    } catch (toggleError) {
      showError(remoteOperationMessage(toggleError, 'Não foi possível alterar a categoria.'));
    }
  };

  return (
    <div className="account-page categories-page">
      <AccountPageHeader title="Categorias" onBack={() => navigate(-1)} />
      <main className="account-content categories-content">
        <PageIntro description="Organize categorias próprias para esta conta sem perder o histórico das movimentações." />
        <section className="categories-surface" aria-labelledby="custom-categories-title">
          <div className="categories-surface__heading">
            <div><span>Conta atual</span><h1 id="custom-categories-title">Categorias personalizadas</h1></div>
            {canManage && <button type="button" onClick={() => setEditor({})}><PiPlus aria-hidden="true" /> Nova categoria</button>}
          </div>
          {loading ? <p className="categories-status" role="status">Carregando categorias...</p> : error ? (
            <p className="categories-status" role="alert">Não foi possível carregar as categorias.</p>
          ) : customCategories.length === 0 ? (
            <div className="categories-empty"><PiTag aria-hidden="true" /><strong>Nenhuma categoria personalizada</strong><p>As categorias padrão do Nara continuam disponíveis.</p></div>
          ) : (
            <ul className="categories-list">
              {customCategories.map((category) => (
                <li key={category.id} className={!category.active ? 'is-inactive' : ''}>
                  <span className="categories-list__icon" aria-hidden="true"><PiTag /></span>
                  <span className="categories-list__copy"><strong>{category.name}</strong><small>{TYPE_LABELS[category.type] || category.type}{!category.active ? ' · Desativada' : ''}</small></span>
                  {canManage && <span className="categories-list__actions">
                    <button type="button" onClick={() => setEditor(category)} aria-label={`Editar ${category.name}`}><PiPencilSimple /></button>
                    <button type="button" onClick={() => toggleActive(category)} aria-label={`${category.active ? 'Desativar' : 'Ativar'} ${category.name}`}>{category.active ? <PiToggleRight /> : <PiToggleLeft />}</button>
                  </span>}
                </li>
              ))}
            </ul>
          )}
          {!canManage && <p className="categories-status">Seu acesso permite somente visualizar as categorias.</p>}
        </section>
      </main>
      {editor && <CategoryFormSheet category={editor.id ? editor : null} onSave={saveCategory} onClose={() => setEditor(null)} />}
    </div>
  );
};

export default Categories;
